import { AutomaticDiscountGetDto } from 'app/shared/models/automatic-discount.models';
import { MediaDTO, MediaShop } from 'app/shared/models/media.models';
import { TagDTO } from 'app/shared/models/tag.models';

import { ShopFilterItem } from './filter-item.model';
import { ProductSize } from './product-details-availability.model';
import { ProductVariant } from './product-variant';
import { GenderTypeEnum } from 'app/back-office/shared/enum/gender-type.enum.model';
import { PaletteLimitDTO } from 'app/shared/models/palette-limit.model';

export class ProductCard {
  id: number;
  name: string;
  storeId: number;
  code: string;
  extCode?: string;
  title: string;
  externalId: number;
  isContinuative: boolean;
  isCustomizableWithPlayers: boolean;
  description: string;
  minimumWholesalePrice: number;
  minimumRetailPrice: number;
  maxWholesalePrice: number;
  maxRetailPrice: number;
  descriptionEnglish: string;
  variants: ProductVariant[];
  media: MediaShop[];
  tags?: TagDTO[];
  seasons: ShopFilterItem[];
  purposes: ShopFilterItem[];
  promotions: AutomaticDiscountGetDto[];
  gender: GenderTypeEnum;
  paletteLimits: PaletteLimitDTO[];
  merchantName: string;
  price: number;
  variantsTotalAvailableQuantity?: number;

  pricesSummary: ProductPricesSummary;

  public styleGroupId: number;

  private _mockedLogoUrl: string;

  // getter and setter for logo Url
  get logoUrl(): string {
    // if (this._mockedLogoUrl)
    //   return this._mockedLogoUrl;
    // if (this.media && this.media[0])
    //   return this.media[0]?.src;
    // return this.variants.find(x => x?.media?.length > 0)?.media[0]?.src || DEFAULT_PRODUCT_IMAGE
    return '';
  }

  set logoUrl(value: string) {
    this._mockedLogoUrl = value;
  }

  // get price which will be displayed on a product card
  get productFromPrice(): number {
    return this.pricesSummary?.minimumWholesalePrice || 0;
  }

  // returns list of Media which will be shown on product card
  get variantsMiniatures(): Array<MediaDTO> {
    return this.disctintcPalletsVariants?.map(x => x.media[0]).filter(x => !!x).slice(0, 9);
  }

  // returns list of all Media for each pallete
  get allVariantsMiniatures(): Array<MediaDTO> {
    const distinct = this.disctintcPalletsVariants.slice(0);
    return distinct.map(x => x.media[0]).filter(x => !!x);
  }

  // returns number of media which are not shown on a product card
  get additionalVariantMiniatures(): number {
    return this.disctintcPalletsVariants?.map(x => x.media[0]).filter(x => !!x).length - 9;
  }
  // returns list of Media which will be shown on small product card
  get variantsMiniaturesSmall(): Array<MediaDTO> {
    return this.disctintcPalletsVariants?.map(x => x.media[0]).slice(0, 3).filter(x => !!x);
  }

  // returns number of media which are not shown on a small product card
  get additionalVariantMiniaturesSmall(): number {
    return this.disctintcPalletsVariants?.map(x => x.media[0]).filter(x => !!x).length - 3;
  }

  // returns a list of Variants, disctinct by palleteId field
  private get disctintcPalletsVariants(): Array<ProductVariant> {
    const uniquePalletsId = [...new Set(this.variants?.map(item => item.paletteId))];
    return uniquePalletsId.map(palleteId => this.variants.find(variant => variant.paletteId === palleteId));
  }

  // returns list of ProductSize which will be shown on product card
  get variantsSizes(): Array<ProductSize> {
    return this.disctinctVariantSizes?.map(x => new ProductSize({ ...x.size, availableQuantity: x.availableQuantity })).slice(0, 9).filter(x => !!x).sort((a, b) => a.sizeOrder - b.sizeOrder);
  }

  // returns number of ProductSize which are not shown on a product card
  get additionalVariantSizes(): number {
    return this.disctinctVariantSizes?.map(x => x.size).filter(x => !!x).length - 9;
  }

  // returns the smallest available ProductSize
  get lowestAvailableSize(): ProductSize {
    const sizes = this.variantsSizes.filter(x => !x.availableQuantity);
    return sizes.length ? sizes[0] : null;
  }

  // returns the biggest available ProductSize
  get biggestAvailableSize(): ProductSize {
    const sizes = this.variantsSizes.filter(x => !x.availableQuantity);
    return sizes.length ? sizes[sizes.length - 1] : null;
  }

  //returns list of Variants, disctinct by sizeId field
  private get disctinctVariantSizes(): Array<ProductVariant> {
    const uniqueSizesId = [...new Set(this.variants?.map(item => item.sizeId))];
    return uniqueSizesId.map(sizeId => this.variants.find(variant => variant.sizeId === sizeId)).filter(x => x?.size?.name);
  }

  get seasonsLabel(): string {
    return this.seasons?.map(x => x.name).reverse().join(' | ');
  }

  get purposeLabel(): string {
    return this.purposes?.map(x => x.name).join(' | ');
  }

  constructor(s?: Partial<ProductCard>) {
    if (s) {
      Object.assign(<ProductCard>this, s);

      if (s.pricesSummary) {
        this.pricesSummary = new ProductPricesSummary(s.pricesSummary);
      }

      if (s.tags) {
        this.tags = s.tags.map(x => new TagDTO(x));
      }
      if (s.variants) {
        this.variants = s.variants.map(x => new ProductVariant(x));
      }
      if (s.media) {
        this.media = s.media.map(x => new MediaShop(x));
      }

    }
    if (!this.pricesSummary) {
      this.pricesSummary = new ProductPricesSummary({
        minimumRetailPrice: 0,
        minimumWholesalePrice: 0,
        maxRetailPrice: 0,
        maxWholesalePrice: 0
      });
    }
  }
}

export class ProductPricesSummary {
  maxRetailPrice: number;
  maxWholesalePrice: number;
  minimumRetailPrice: number;
  minimumWholesalePrice: number;
  showFrom?: boolean;
  storeChannelDiscount?: number;

  constructor(s?: Partial<ProductPricesSummary>) {
    if (s) {
      Object.assign(<ProductPricesSummary>this, s);
    }
  }
}
